import { useEffect, useId, useRef, useState, type FC } from 'react'
import { Icon } from './Icon.js'

export interface MenuItem {
  readonly key: string
  readonly label: string
  readonly onSelect: () => void
  readonly danger?: boolean
}

export interface MenuButtonProps {
  label?: string
  items: readonly MenuItem[]
  disabled?: boolean
}

/**
 * The actions a screen offers but does not lead with. One row of buttons is
 * the most a header can carry before it wraps into two and stops reading as a
 * header, so everything past the leading actions waits in here.
 */
export const MenuButton: FC<MenuButtonProps> = ({ label = 'More', items, disabled }) => {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const menuId = useId()

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (items.length === 0) return null

  return (
    <div className="menu" ref={root}>
      <button
        type="button"
        className="btn btn--ghost"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="btn__icon"><Icon name="dots" size={18} /></span>
        {label}
      </button>
      {open ? (
        <div className="menu__list" role="menu" id={menuId}>
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className={item.danger ? 'menu__item menu__item--danger' : 'menu__item'}
              onClick={() => { setOpen(false); item.onSelect() }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
